import { ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { OrgSwitcher } from './OrgSwitcher';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // Build org-prefixed paths
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';

  const hideSidebar = location.pathname.includes('/wrapped') || location.pathname.includes('/rundown/');
  const isFullHeight = location.pathname.includes('/rundown/');

  // Full-height layout for rundown editor (no scroll on body)
  if (isFullHeight) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
        <header className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={orgPrefix || '/'} className="text-xl font-bold text-gray-900">
                AI News Assist
              </Link>
              <OrgSwitcher />
            </div>
            <nav className="flex items-center gap-4">
              <Link to={orgPrefix || '/'} className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Feed
              </Link>
              <Link to={`${orgPrefix}/rundowns`} className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Rundowns
              </Link>
            </nav>
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={orgPrefix || '/'} className="text-xl font-bold text-gray-900">
              AI News Assist
            </Link>
            <OrgSwitcher />
          </div>
          <nav className="flex items-center gap-4">
            <Link to={orgPrefix || '/'} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Feed
            </Link>
            <Link to={`${orgPrefix}/tuples`} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Knowledge Graph
            </Link>
            <Link to={`${orgPrefix}/materials`} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Materials
            </Link>
            <Link to={`${orgPrefix}/sitemaps`} className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Sitemaps
            </Link>
            <Link to={`${orgPrefix}/wrapped`} className="text-sm font-medium text-purple-600 hover:text-purple-700">
              Today, Wrapped
            </Link>
          </nav>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {!hideSidebar && <Sidebar />}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
